import {
  AGREEMENT_TYPE,
  IAgreementTemplateResponse,
} from 'services/templates/agreement-template/types';
import {
  ICustomSectionTemplateResponse,
} from 'services/templates/section-template/types';
import {
  IContractTemplateTreeItemForm,
  IDistributionResponse,
  IDistributionType,
  SETTLEMENT_TYPE,
  TEMPLATE_TYPE,
} from 'services/templates/types';
import {
  getDefaultShareFormValue,
  getSharePercentFromResponse,
  inferSettlementTypeFromDistribution,
} from 'utils/components/contract-template/distribution/settlementType.utils';
import { parseAgreementType } from 'utils/components/contract-template/agreement/agreementTemplate.utils';
import { isParagraphCustomSection } from 'utils/components/contract-template/section/customSection.utils';

export const distributionSectionQueryToForm = (
  distributionSection: IDistributionResponse,
  lowestId: number,
  type: IDistributionType
): IContractTemplateTreeItemForm => {
  const settlementType = inferSettlementTypeFromDistribution(distributionSection);

  const distributionChildren: IContractTemplateTreeItemForm[] =
    distributionSection.shares.map(
      ({ salesChannelId, percentage, percent, amountPerUnit, salesChannelName }, key) => ({
        id: `share-${TEMPLATE_TYPE[type]}-${lowestId + key + 1}`,
        type: TEMPLATE_TYPE.agreement,
        settlementType,
        content: {
          type: AGREEMENT_TYPE.shares,
          distributionTemplateShares: {
            id: salesChannelId,
            name: salesChannelName ?? '',
            ...getDefaultShareFormValue(settlementType, {
              percent: getSharePercentFromResponse({ percent, percentage }),
              amountPerUnit,
            }),
          },
        },
        children: [],
      })
    );

  distributionChildren.unshift({
    id: `default-share-${TEMPLATE_TYPE[type]}`,
    type: TEMPLATE_TYPE.agreement,
    settlementType,
    content: {
      type: AGREEMENT_TYPE.defaultShare,
      distributionTemplateShares: {
        id: `default-share-agreement-${TEMPLATE_TYPE[type]}`,
        name: '',
        ...getDefaultShareFormValue(settlementType, {
          percent: distributionSection.defaultSharePercentage,
          amountPerUnit: distributionSection.defaultAmountPerUnit,
        }),
      },
    },
    children: [],
    disabledSelection: true,
  });

  const distribution: IContractTemplateTreeItemForm = {
    id: `${lowestId}`,
    type: type,
    content: distributionSection.title,
    settlementType,
    children: distributionChildren,
  };

  return distribution;
};

interface IConnectContractTreeItems {
  customSections?: ICustomSectionTemplateResponse[];
  digitalDistribution?: IDistributionResponse;
  physicalDistribution?: IDistributionResponse;
  merchDistribution?: IDistributionResponse;
  channelDistribution?: IDistributionResponse;
  agreements?: IAgreementTemplateResponse[];
  isParagraphContext?: boolean;
}

export const connectTemplateTreeItems = (
  contractItems: IConnectContractTreeItems,
  lowestAvailableId?: string
): IContractTemplateTreeItemForm[] => {
  const {
    agreements,
    customSections,
    digitalDistribution,
    physicalDistribution,
    merchDistribution,
    channelDistribution,
    isParagraphContext = false,
  } = contractItems;

  const treeItems: IContractTemplateTreeItemForm[] = [];

  let lowestId = lowestAvailableId ? parseInt(lowestAvailableId) + 1 : 0;

  if (agreements?.length) {
    for (const agreement of agreements) {
      const { id, position, type, ...formAgreement } = agreement;
      const agreementType = parseAgreementType(type);

      if (agreementType === AGREEMENT_TYPE.paragraph || isParagraphContext) {
        treeItems[position] = {
          id: `${lowestId}`,
          type: TEMPLATE_TYPE.agreement,
          children: [],
          content: {
            type: AGREEMENT_TYPE.paragraph,
            paragraphAgreementTemplate: formAgreement,
            textAgreementTemplate: null,
            selectAgreementTemplate: null,
          },
        };
      } else if (agreementType === AGREEMENT_TYPE.text) {
        treeItems[position] = {
          id: `${lowestId}`,
          type: TEMPLATE_TYPE.agreement,
          children: [],
          content: {
            type: AGREEMENT_TYPE.text,
            selectAgreementTemplate: {
              ...formAgreement,
              options: [formAgreement.defaultValue],
            },
            textAgreementTemplate: formAgreement,
          },
        };
      } else if (agreementType === AGREEMENT_TYPE.select) {
        const { options, ...textAgreement } = formAgreement;
        treeItems[position] = {
          id: `${lowestId}`,
          type: TEMPLATE_TYPE.agreement,
          children: [],
          content: {
            type: AGREEMENT_TYPE.select,
            textAgreementTemplate: textAgreement,
            selectAgreementTemplate: {
              ...formAgreement,
              options: options ?? [],
            },
          },
        };
      }

      lowestId += 1;
    }
  }

  if (digitalDistribution && !!digitalDistribution.title.length) {
    const digitalDistributionItem = distributionSectionQueryToForm(
      digitalDistribution,
      lowestId,
      TEMPLATE_TYPE.digitalDistribution
    );
    treeItems[digitalDistribution.position] = digitalDistributionItem;
    lowestId += digitalDistributionItem.children.length;
  }

  if (physicalDistribution && !!physicalDistribution.title.length) {
    const physicalDistributionItem = distributionSectionQueryToForm(
      physicalDistribution,
      lowestId,
      TEMPLATE_TYPE.physicalDistribution
    );
    treeItems[physicalDistribution.position] = physicalDistributionItem;
    lowestId += physicalDistributionItem.children.length;
  }
  if (merchDistribution && !!merchDistribution.title.length) {
    const merchDistributionItem = distributionSectionQueryToForm(
      merchDistribution,
      lowestId,
      TEMPLATE_TYPE.merchDistribution
    );
    treeItems[merchDistribution.position] = merchDistributionItem;
    lowestId += merchDistributionItem.children.length;
  }
  if (channelDistribution && !!channelDistribution.title.length) {
    const channelDistributionItem = distributionSectionQueryToForm(
      channelDistribution,
      lowestId,
      TEMPLATE_TYPE.channelDistribution
    );
    treeItems[channelDistribution.position] = channelDistributionItem;
    lowestId += channelDistributionItem.children.length;
  }

  if (customSections?.length) {
    for (const section of customSections) {
      const isParagraphSection = isParagraphCustomSection(section.type);
      const children = connectTemplateTreeItems(
        {
          agreements: section.agreements,
          customSections: section.customSections,
          isParagraphContext: isParagraphSection,
        },
        `${lowestId + 1}`
      );

      treeItems[section.position] = {
        id: `${lowestId}`,
        type: isParagraphSection
          ? TEMPLATE_TYPE.paragraphSection
          : TEMPLATE_TYPE.section,
        content: section.title,
        children: children,
      };

      lowestId += children.length + 2;
    }
  }

  return treeItems;
};
